import { useValue } from "@legendapp/state/react";
import { formatBytes } from "@repo/shared/helpers/format-bytes";
import { formatCount } from "@repo/shared/helpers/format-count";

import { runProgress$ } from "./run-progress";

/**
 * Rows read so far by a dataset's in-flight streaming publish, e.g. `"50k rows"`,
 * for a site with room for one number. `null` when nothing is streaming.
 */
export const useRunProgressRows = (
  datasetId: string | null | undefined,
): string | null =>
  useValue(() => {
    if (!datasetId) return null;
    const progress = runProgress$[datasetId].get();
    if (!progress) return null;

    return `${formatCount(progress.rowCount)} rows`;
  });

/**
 * Counters for a dataset's in-flight streaming publish, e.g.
 * `"1.2m rows · 240 MB"`. `null` when nothing is streaming — the norm for
 * buffered drivers and for short publishes that never emit a progress tick.
 *
 * Counters are absolute: the total is unknown until the last row arrives, so
 * there is deliberately no percentage or bar to render.
 */
export const useRunProgressCounters = (
  datasetId: string | null | undefined,
): string | null =>
  useValue(() => {
    if (!datasetId) return null;
    const progress = runProgress$[datasetId].get();
    if (!progress) return null;

    return `${formatCount(progress.rowCount)} rows · ${formatBytes(
      progress.sizeBytes,
    )}`;
  });

/**
 * {@link useRunProgressCounters} phrased for a site that has no other wording
 * of its own, e.g. `"Streaming 1.2m rows · 240 MB"` in a spinner tooltip.
 * `null` when nothing is streaming, so the caller keeps its plain spinner.
 */
export const useRunProgressLabel = (
  datasetId: string | null | undefined,
): string | null => {
  const counters = useRunProgressCounters(datasetId);
  return counters === null ? null : `Streaming ${counters}`;
};
